import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function AllBranchesSectionAttendanceReport() {
  const [programs, setPrograms] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [percentage, setPercentage] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [logoBase64, setLogoBase64] = useState(null);
  const [datesGrid, setDatesGrid] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (message) => {
    setErrorMessage(message);
  };

  const handleGetDates = () => {
    setErrorMessage("");
    setDatesLoading(true);
    axios
      .get(`${API_BASE_URL}/api/allbranches-section-attendancereport/posted-dates-grid`, {
        params: {
          client_id: clientId,
          college_id: collegeId,
          program_id: selectedProgram,
          admittedbatch_id: selectedBatch,
          from_date: fromDate,
          to_date: toDate,
        },
      })
      .then((res) => {
        setDatesGrid(res.data.data || []);
      })
      .catch((err) => {
        setDatesGrid([]);
        showError(err.response?.data?.message || "Unable to load posted attendance dates.");
      })
      .finally(() => setDatesLoading(false));
  };

  // Get user info from session
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  useEffect(() => {
    if (clientId && collegeId) {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/api/attendancereport/programs/${clientId}/${collegeId}`)
        .then((res) => setPrograms(res.data.data || []))
        .catch(() => {
          setPrograms([]);
          showError("Unable to load programs. Please check the server connection.");
        })
        .finally(() => setLoading(false));
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/allbranches-section-attendancereport/batches/${clientId}/${collegeId}/${selectedProgram}`
        )
        .then((res) => setBatches(res.data.data || []))
        .catch(() => {
          setBatches([]);
          showError("Unable to load batches. Please check the server connection.");
        })
        .finally(() => setLoading(false));
      setSelectedBatch("");
    }
  }, [selectedProgram, clientId, collegeId]);

  const handleGetReport = () => {
    setErrorMessage("");
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/api/allbranches-section-attendancereport/report`, {
        params: {
          client_id: clientId,
          college_id: collegeId,
          program_id: selectedProgram,
          admittedbatch_id: selectedBatch,
          from_date: fromDate,
          to_date: toDate,
          percentage,
          amount,
        },
      })
      .then((res) => {
        setReportData(res.data.data || []);
      })
      .catch((err) => {
        setReportData([]);
        showError(err.response?.data?.message || "Unable to load attendance summary.");
      })
      .finally(() => setLoading(false));
  };

  const handleDownloadPDF = async () => {
    try {
      const params = {
        client_id: clientId,
        college_id: collegeId,
        program_id: selectedProgram,
        admittedbatch_id: selectedBatch,
        from_date: fromDate,
        to_date: toDate,
        percentage,
        amount,
        logoBase64,
      };
      const response = await fetch(
        `${API_BASE_URL}/api/allbranches-section-attendancereport/download-pdf`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/pdf" },
          body: JSON.stringify(params),
        }
      );
      if (!response.ok) throw new Error("Failed to download PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "AllBranchesSectionAttendanceReport.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download PDF: " + err.message);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Toolbar>
        <Typography variant="h6">All Branches & Section Attendance Report</Typography>
      </Toolbar>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      )}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Program"
          name="program"
          select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Program</option>
          {programs.map((p) => (
            <option key={p.program_id} value={p.program_id}>
              {p.program_name}
            </option>
          ))}
        </TextField>
        <TextField
          label="Batch"
          name="batch"
          select
          value={selectedBatch}
          onChange={(e) => setSelectedBatch(e.target.value)}
          size="medium"
          sx={{ minWidth: 180 }}
          disabled={!selectedProgram}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ native: true }}
        >
          <option value="">Select Batch</option>
          {batches.map((batch) => (
            <option key={batch.admittedbatch_id} value={batch.admittedbatch_id}>
              {batch.admittedbatch}
            </option>
          ))}
        </TextField>
        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          size="medium"
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          label="Percentage"
          value={percentage}
          onChange={(e) => setPercentage(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
        />
        <TextField
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          size="medium"
          sx={{ minWidth: 100 }}
        />
        <Button
          variant="contained"
          onClick={handleGetReport}
          disabled={loading}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          {loading ? <CircularProgress size={24} /> : "Get Report"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleGetDates}
          disabled={datesLoading}
          sx={{ minWidth: 120, color: "#000", borderColor: "#000" }}
        >
          {datesLoading ? (
            <CircularProgress size={24} />
          ) : (
            <span style={{ color: "#000" }}>Get Dates</span>
          )}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDownloadPDF}
          sx={{ color: "#fff", minWidth: 120 }}
        >
          Download PDF
        </Button>
      </Box>
      {/* Display posted attendance dates grid if available */}
      {datesGrid.length > 0 && (
        <Box sx={{ my: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Attendance Posted Dates Grid
          </Typography>
          <Box sx={{ height: 400, width: "100%", mt: 1 }}>
            <DataGrid
              rows={datesGrid.map((row, idx) => ({ id: idx, ...row }))}
              columns={[
                { field: "program", headerName: "Program", width: 180 },
                { field: "batch", headerName: "Batch", width: 140 },
                { field: "branch", headerName: "Branch", width: 180 },
                { field: "section", headerName: "Section", width: 100 },
                {
                  field: "dates",
                  headerName: "Dates",
                  width: 250,
                  renderCell: (params) => params.row.dates.join(", "),
                },
                { field: "numberOfDates", headerName: "Number of Dates", width: 140 },
              ]}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              loading={datesLoading}
              getRowId={(row) => row.id}
              disableSelectionOnClick
            />
          </Box>
        </Box>
      )}
      <Box sx={{ my: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Branch & Section Attendance Summary
        </Typography>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            rows={reportData.map((r, idx) => ({
              id: idx,
              ...r,
              program: (() => {
                const selected = programs.find((p) => p.program_id == selectedProgram);
                return selected ? selected.program_name : "";
              })(),
              batch: (() => {
                const selected = batches.find((b) => b.admittedbatch_id == selectedBatch);
                return selected ? selected.admittedbatch : "";
              })(),
            }))}
            columns={[
              { field: "program", headerName: "Program", width: 180 },
              { field: "batch", headerName: "Batch", width: 140 },
              {
                field: "branch",
                headerName: "Branch",
                width: 150,
                valueGetter: (params) => params.row.branch?.branch_name,
              },
              { field: "section", headerName: "Section", width: 100 },
              {
                field: "totalStudents",
                headerName: "Total Students",
                width: 120,
                valueGetter: (params) => params.row.report?.length,
              },
              // Add more summary columns as needed
            ]}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            loading={loading}
            getRowId={(row) => row.id}
            disableSelectionOnClick
          />
        </Box>
      </Box>
    </Paper>
  );
}
